import ChangeHistoryIcon from '@mui/icons-material/ChangeHistory';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SyncIcon from '@mui/icons-material/Sync';
import { Box, Button, ClickAwayListener, IconButton, Popper, Stack, Tooltip, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import classnames from "classnames";
import React, { useCallback, useContext, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { HeaderIcons } from '../../../contexts/TablesContext/DashboardsTableProvider';
import { WorkspacesContext } from '../../../contexts/WorkspacesContext';
import theme from "../../../theme";
import InvestorsMultiAction from "../multi-action/InvestorsMultiAction";
import WorkspaceTableFilter from './WorkspaceTableFilter';

export type WorkspaceFilters = {
    sectors?: string[],
    stages?: string[],
    geography?: string[],
};

const useStyles = makeStyles((theme) => ({
    row: {
        minWidth: '100%',
        width: 'fit-content',
        height: 'fit-content',
        borderBottom: `1px solid ${theme.colors.neutral['400']}`,
        background: `#fff`,
    },
    header: {
        fontFamily: 'Inter',
        fontSize: '1.1rem',
        fontWeight: '600',
        textTransform: 'none',
        color: theme.colors.neutral['600'],
        paddingLeft: 'unset !important',
        "&:hover": {
            background: 'unset',
        }
    },
    hoverHeader: {
        color: theme.colors.neutral['600'],
        cursor: 'pointer',
        "&:hover": {
            color: theme.colors.neutral['900'],
            "& > svg": {
                color: theme.colors.neutral['900'],
                stroke: `${theme.colors.neutral['900']} !important`,
            },
        },
    },
    checkbox: {
        minWidth: 24,
        maxWidth: 24,
    },
    cell: {
        minWidth: 180,
        maxWidth: 180,
        minHeight: 60,
        maxHeight: 60,
        padding: '0 10px',
    },
    narrowCell: {
        minWidth: 150,
        maxWidth: 150,
    },
    narrowerCell: {
        minWidth: 50,
        maxWidth: 50,
    },
    customCell: {
        minWidth: 240,
        maxWidth: 240,
    },
    customNarrowCell: {
        minWidth: 20,
        maxWidth: 20,
    },
    wideCell: {
        minWidth: 280,
        maxWidth: 280,
    },
    widerCell: {
        minWidth: 380,
        maxWidth: 380,
    },
    fullWidthCell: {
        minWidth: 0,
        maxWidth: 'unset !important',
        width: '100%',
    },
    hiddenCell: {
        display: 'none',
    },
    endCell: {
        minWidth: 4,
        maxWidth: 4,
        padding: 'unset',
    },
    commonButton: {
        height: 36,
        borderRadius: 32,
        textTransform: 'none',
        fontSize: '0.95rem',
        fontWeight: 'bold',
    },
    icon: {
        width: 20,
        height: 20,
    },
    clear: {
        minWidth: 'unset',
        fontFamily: 'Inter',
        fontSize: '0.95rem',
        color: theme.palette.error.main,
        textTransform: 'none',
        "&:hover": {
            background: 'unset',
        }
    },
}));

export const TableHeaders = ['Name', 'Fit score', 'Companies', 'Investors', 'Workspace Admin', 'Email', '[1]', 'Sectors', 'Stage', 'Geography', 'In this workspace', 'Associated tags', '[company match]', '[2]', '[added]', '[invite]', '[menu]'];
// export const InvestorTableHeaders = ['Name', 'Email', '[1]', 'Sectors', 'Stage', 'Geography', '[2]', '[menu]'];
export const InvestorTableHeaders = ['Name', 'Email', '[2]', '[menu]'];
export const SharedTableHeaders = ['Name', 'Investors', '[2]', '[added]', '[menu]'];
export const InvitationTableHeaders = ['Name', 'Investors', '[2]', '[invite]'];
export const OverviewTableHeaders = ['Name', 'Investors', '[2]', '[menu]'];

const WorkspacesTableHeader: React.FC<{
    columns: string[],
    filters?: WorkspaceFilters,
    multiAction?: boolean,
    findInvestor?: boolean,
    onColumnSort?: (column: string) => void,
    onFilter?: ({ sectors, stages, geography } : WorkspaceFilters) => void,
    onRefreshScores?: () => void,
}> = ({ columns, filters, multiAction, findInvestor, onColumnSort, onFilter, onRefreshScores }) => {
    const classes = useStyles();
    const { matchedAssociatedDashboards, setMatchedAssociatedDashboards } = useContext(WorkspacesContext);
    const multiActionRef = useRef<any>(null);

    const [hoverAnchor, setHoverAnchor] = useState<HTMLElement|null>();
    const [hoverField, setHoverField] = useState<string|undefined>(undefined);
    const [sortBy, setSortBy] = useState<string>('Added');

    const anchorRefs = useRef<HTMLDivElement[]>([]);

    const showColumn = useCallback((column: string) => columns?.includes(column), [columns]);

    const handleColumnSort = useCallback((column: string) => {
        if (column === 'Fit score')
            setSortBy(`${column} +[${uuidv4()}]`);
        else
            setSortBy(column);
        onColumnSort?.(column);
    }, [onColumnSort]);

    const handleHover = useCallback((anchor: HTMLElement|null, field: string|null) => {
        setHoverAnchor(anchor);
        setHoverField(field || '');
    }, []);

    return (<>
        <Stack direction="row" className={classes.row} alignItems="flex-start" justifyContent="flex-start" ref={multiActionRef}>
            <Stack className={classes.checkbox} />
            {TableHeaders.map((header, i, self) => showColumn(header) && (
                <Stack direction="column" className={classnames(classes.cell,
                    (i === 0) && (findInvestor ? classes.wideCell : classes.customCell),
                    (i === 2) && classes.customCell,
                    (i === 6) && classes.customNarrowCell,
                    (i === 7) && classes.wideCell,
                    (i === 8) && classes.wideCell,
                    (i === 9) && classes.wideCell,
                    (i === 10) && classes.widerCell,
                    (i === 11) && classes.widerCell,
                    (i === 12) && classes.wideCell,
                    (i === 15) && classes.widerCell,
                    (i === self.length - 4) && classes.fullWidthCell,
                    (i === self.length - 3) && classes.customCell,
                    (i === self.length - 1) && classes.narrowerCell)}
                    alignItems={(i === self.length - 1) ? 'flex-end' : 'flex-start'}
                    justifyContent="center" key={'workspaces-table-header-83-' + i}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" width="100%"
                        ref={el => anchorRefs.current[i] = el as HTMLDivElement}>
                        {(!!onColumnSort && ['Fit score'].includes(header)) ? (
                            <Button variant="text"
                                className={classes.header}
                                onClick={() => handleColumnSort(header)}
                                disableRipple>
                                {header}
                                {sortBy.includes(header) ? (
                                    <ArrowDropDownIcon sx={{ fill: theme.palette.primary.main, transform: 'scale(1.5, 2.5)', translate: '4px 0px'}} />
                                ) : (
                                    <ChangeHistoryIcon sx={{ fill: theme.colors.neutral['600'],
                                        transform: 'rotate(180deg) scale(0.85, 0.85)', translate: '4px 1px' }} />
                                )}
                            </Button>
                        ) : (!header.startsWith('[') && !header.endsWith(']')) && (<>{
                            Object.keys(HeaderIcons).includes(header) ? (
                                <Stack direction="row" className={classnames(classes.header, classes.hoverHeader)}
                                    spacing={1} alignItems="center" justifyContent="flex-start"
                                    onClick={() => handleHover(anchorRefs.current[i], header)}>
                                    {!!HeaderIcons[header] && HeaderIcons[header]}
                                    <span>{header}</span>
                                </Stack>
                        ) : (
                            <Typography className={classes.header}
                                sx={{ color: theme.colors.neutral['600'],
                                    cursor: 'auto',
                                }}> {header} </Typography>
                        )}</>)}
                        {!!filters && ((header === 'Sectors') && (!!filters.sectors?.length ? (
                            <Stack direction="row" alignItems="center" justifyContent="flex-start">
                                <FilterAltIcon sx={{ width: 24, height: 24, color: theme.palette.primary.main }} />
                                <Button variant="text"
                                    className={classes.clear}
                                    onClick={() => onFilter?.({ sectors: [] })}
                                    disableRipple> {'clear'} </Button>
                            </Stack>
                        ) : ( <ExpandMoreIcon onClick={() => handleHover(anchorRefs.current[i], header)}
                                sx={{ color: theme.colors.neutral['600'],
                                    cursor: 'pointer',
                                    "&:hover": { color: theme.colors.neutral['900'] },
                                }} />
                        )))}
                        {!!filters && ((header === 'Stage') && (!!filters.stages?.length ? (
                            <Stack direction="row" alignItems="center" justifyContent="flex-start">
                                <FilterAltIcon sx={{ width: 24, height: 24, color: theme.palette.primary.main }} />
                                <Button variant="text"
                                    className={classes.clear}
                                    onClick={() => onFilter?.({ stages: [] })}
                                    disableRipple> {'clear'} </Button>
                            </Stack>
                        ) : ( <ExpandMoreIcon onClick={() => handleHover(anchorRefs.current[i], header)}
                                sx={{ color: theme.colors.neutral['600'],
                                    cursor: 'pointer',
                                    "&:hover": { color: theme.colors.neutral['900'] },
                                }} />
                        )))}
                        {!!filters && ((header === 'Geography') && (!!filters.geography?.length ? (
                            <Stack direction="row" alignItems="center" justifyContent="flex-start">
                                <FilterAltIcon sx={{ width: 24, height: 24, color: theme.palette.primary.main }} />
                                <Button variant="text"
                                    className={classes.clear}
                                    onClick={() => onFilter?.({ geography: [] })}
                                    disableRipple> {'clear'} </Button>
                            </Stack>
                        ) : ( <ExpandMoreIcon onClick={() => handleHover(anchorRefs.current[i], header)}
                                sx={{ color: theme.colors.neutral['600'],
                                    cursor: 'pointer',
                                    "&:hover": { color: theme.colors.neutral['900'] },
                                }} />
                        )))}
                        {(!!Object.entries(matchedAssociatedDashboards).length && ['[company match]'].includes(header)) && (<>
                            <Button variant="text"
                                className={classes.clear}
                                onClick={() => setMatchedAssociatedDashboards({})}
                                disableRipple> {'Clear matches'} </Button>
                        </>)}
                        {!!onRefreshScores && (header === 'Fit score') && (<>
                            <Tooltip placement="top-start" title={
                                <Typography component="span" fontSize="0.95rem" fontWeight={500}>
                                    {`Calculate fit scores for each company based on each workspace's preferences.`}
                                </Typography>}
                                componentsProps={{tooltip:{sx:{
                                    display: 'flex', alignItems: 'center', borderRadius: '12px', transform: 'translate(-90px) !important',
                                    backgroundColor: 'rgba(150, 150, 150, 0.95)', width: '210px', height: '110px',  padding: 2,
                                }}}}>
                                <IconButton onClick={() => onRefreshScores?.()} sx={{ padding: 'unset' }} disableRipple>
                                    <SyncIcon sx={{ fill: theme.palette.primary.main, transform: 'scale(1.05, 1.05)', translate: '-5px 1px' }} />
                                </IconButton>
                            </Tooltip>
                        </>)}
                    </Stack>
                </Stack>
            ))}
            <Stack className={classnames(classes.cell, classes.endCell)} />
        </Stack>
        <Popper open={!!hoverAnchor && !!hoverField}
            anchorEl={hoverAnchor} sx={{ zIndex: 2000 }}
            placement="bottom-start">
            <ClickAwayListener onClickAway={() => handleHover(null, null)}>
                <Box width="fit-content" height="fit-content" >
                    {['Sectors', 'Stage', 'Geography'].includes(hoverField!) && (
                        <WorkspaceTableFilter
                            filters={filters}
                            filterType={hoverField}
                            onFilter={onFilter}
                            readOnly={false} />
                    )}
                </Box>
            </ClickAwayListener>
        </Popper>
        {multiAction && (<InvestorsMultiAction ref={multiActionRef} />)}
    </>);
}

export default WorkspacesTableHeader;